import { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
// @ts-ignore
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate'
import moment from 'moment'
import {
  PaymentTerm,
  CurrentCompanyInfoInfoResponseDto,
  Quote2ServiceDto,
  BOOKING_TYPE
} from '../../../../../definition'
import { asyncGetStaticData } from '../../../../../services/booking.service'
import { asyncGetCompanyInfor } from '../../../../../services'
import {
  getPaymentTermLlabel,
  estimateLines,
  renderDiscountLabel,
  renderDiscountValue,
  renderDiscountPromo
} from '../../../../../utils'

type table = any

const renderLineSpace = (lines: number) => {
  let emptyArray = []
  for (let i = 0; i < lines; i++) {
    emptyArray.push('')
  }
  return emptyArray
}

export default function QuoteCSV(props: any) {
  const [quoteTerm, setQuoteTerm] = useState<string | undefined>('')
  const [companyInfor, setCompanyInfor] = useState<CurrentCompanyInfoInfoResponseDto>({})
  const { quoteSumUp, services, serviceOptions } = props

  useEffect(() => {
    asyncGetStaticData().then((res) => {
      setQuoteTerm(res.quote_term)
    })
    asyncGetCompanyInfor().then((companyInfor) => {
      setCompanyInfor(companyInfor)
    })
  }, [])

  const getServiceNamebyId = (serviceId: string, serviceList: any) => {
    let serviceName = ''
    serviceList.forEach((service: any) => {
      if (service.value === serviceId) {
        serviceName = service.label
      }
    })
    return serviceName
  }

  const createDownloadData = () => {
    handleExport().then((url: any) => {
      const downloadnAnchorNode = document.createElement('a')
      downloadnAnchorNode.setAttribute('href', url)
      downloadnAnchorNode.setAttribute('download', `quote_detail_${quoteSumUp.quote}`)
      downloadnAnchorNode.click()
      downloadnAnchorNode.remove()
    })
  }

  const s2ab = (s: any) => {
    const buf = new ArrayBuffer(s.length)
    const view = new Uint8Array(buf)
    for (let i = 0; i !== s.length; i++) {
      view[i] = s.charCodeAt(i)
    }
    return buf
  }

  const workbook2blob = (workbook: any) => {
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' })

    const blob = new Blob([s2ab(wbout)], {
      type: 'application/octet-stream'
    })
    return blob
  }

  const handleExport = () => {
    let initSpaces = 1

    // ** Company Infor **
    let companyInforTable: table[] = []
    companyInforTable.push({
      A: `${companyInfor.name}`
    })

    companyInforTable.push({
      A: `${companyInfor.addressLine1}`
    })

    companyInforTable.push({
      A: `${companyInfor.addressLine2}`
    })

    companyInforTable.push({
      A: `Singapore, ${companyInfor.postalCode}`
    })

    companyInforTable.push({
      A: `Tel: ${companyInfor.tel}`
    })

    companyInforTable.push({
      A: `Email: ${companyInfor.email}`
    })

    const spaceAfterCompanyInfor = 2

    let generalInfor = [
      {
        A: 'Customer Name',
        B: 'Quote',
        C: 'Service Type',
        D: 'Date'
      }
    ]

    generalInfor.push({
      A: quoteSumUp.customerName,
      B: quoteSumUp.quote,
      C: quoteSumUp.serviceType,
      D: moment(new Date(quoteSumUp.date)).format('DD-MM-YYYY')
    })

    const spaceAfterGeneral = 1

    // ** Services **

    let servicesDetail: table[] = []
    let serviceTableLines: number

    services.forEach((service: any) => {
      let serviceDetail: table[] = []
      serviceDetail.push({
        A: 'Service Name',
        B: 'Service Description',
        C: 'Notes',
        D: 'Quantity'
      })
      serviceDetail.push({
        A: getServiceNamebyId(service.serviceId, serviceOptions),
        B: service.service.description,
        C: service.notes,
        D: service.quantity
      })
      if (quoteSumUp.serviceType === BOOKING_TYPE.ONE_TIME) {
        serviceDetail.push({
          C: `Discount(${renderDiscountLabel(service as Quote2ServiceDto, quoteSumUp.promoCodeUsed)})`,
          D: 'Amount($)'
        })
        serviceDetail.push({
          C: renderDiscountValue(service as Quote2ServiceDto, quoteSumUp.promoCodeUsed),
          D: service.amount
        })
      } else {
        serviceDetail.push({
          A: 'Number of Time Per Year',
          B: 'Contract Duration(Years)',
          C: `Discount(${renderDiscountLabel(service as Quote2ServiceDto, quoteSumUp.promoCodeUsed)})`,
          D: 'Amount($)'
        })
        serviceDetail.push({
          A: service.timesPerYear,
          B: service.durationInYears,
          C: renderDiscountValue(service as Quote2ServiceDto, quoteSumUp.promoCodeUsed),
          D: service.amount
        })
      }

      serviceTableLines = serviceDetail.length
      serviceDetail.push({})

      servicesDetail = [...servicesDetail, ...serviceDetail]
    })

    const spaceAfterEachService = 1
    const spaceAfterServicesDetail = 2

    // ** Summary

    let summary: table[] = []

    summary.push({
      C: 'Subtotal',
      D: quoteSumUp.subtotal
    })
    summary.push({
      C: `Discount${renderDiscountPromo(quoteSumUp.promoCodeUsed)}`,
      D: quoteSumUp.discount
    })
    if (quoteSumUp.gstVal > 0) {
      summary.push({
        C: 'Amount after discount',
        D: quoteSumUp.amountAfterDiscount
      })
    }
    if (quoteSumUp.surcharge) {
      summary.push({
        C: `Surcharge`,
        D: Number(quoteSumUp.surcharge).toFixed(2)
      })
    }
    if (quoteSumUp.serviceCharge) {
      summary.push({
        C: `Platform Fee`,
        D: Number(quoteSumUp.serviceCharge).toFixed(2)
      })
    }
    if (quoteSumUp.gstVal > 0) {
      summary.push({
        C: `GST ${quoteSumUp.gst}%`,
        D: quoteSumUp.gstVal
      })
    }
    summary.push({
      C: 'Total',
      D: quoteSumUp.total
    })

    const spaceAfterSummary = 2

    // ** Payment Terms **

    let paymentTerm: table[] = []

    if (quoteSumUp.paymentTerm === PaymentTerm.PPP) {
      paymentTerm.push({
        A: 'Payment Terms',
        B: 'Partial Payment Percentage(%)'
      })
      paymentTerm.push({
        A: getPaymentTermLlabel(quoteSumUp.paymentTerm),
        B: quoteSumUp.paymentTermValue
      })
    } else if (quoteSumUp.paymentTerm === PaymentTerm.CREDIT) {
      paymentTerm.push({
        A: 'Payment Terms',
        B: 'Duration (days)'
      })
      paymentTerm.push({
        A: getPaymentTermLlabel(quoteSumUp.paymentTerm),
        B: quoteSumUp.paymentTermValue
      })
    } else {
      paymentTerm.push({
        A: 'Payment Terms'
      })
      paymentTerm.push({
        A: getPaymentTermLlabel(quoteSumUp.paymentTerm)
      })
    }

    const spaceAfterPaymentTerm = 2

    // ** Term **

    let term: table[] = []

    term.push({
      A: 'Terms'
    })

    term.push({
      A: quoteTerm
    })

    const finalData = [
      ...renderLineSpace(initSpaces),
      ...companyInforTable,
      ...renderLineSpace(spaceAfterCompanyInfor),
      ...generalInfor,
      ...renderLineSpace(spaceAfterGeneral),
      ...servicesDetail,
      ...renderLineSpace(spaceAfterServicesDetail),
      ...summary,
      ...renderLineSpace(spaceAfterSummary),
      ...paymentTerm,
      ...renderLineSpace(spaceAfterPaymentTerm),
      ...term
    ]

    const wb = XLSX.utils.book_new()

    const sheet = XLSX.utils.json_to_sheet(finalData, {
      skipHeader: true
    })

    XLSX.utils.book_append_sheet(wb, sheet, `quote_detail_${quoteSumUp.quote}`)

    const workbookBlob = workbook2blob(wb)

    const totalLinesBeforeCompanyInfor = initSpaces

    const totalLinesBeforeGeneral = totalLinesBeforeCompanyInfor + companyInforTable.length + spaceAfterCompanyInfor

    const totalLinesBeforeServices = totalLinesBeforeGeneral + generalInfor.length + spaceAfterGeneral

    const getServiceHeaders = (services: any) => {
      let headers: any = {}
      services.forEach((_: any, index: number) => {
        let totalLinesBeforeEachService = totalLinesBeforeServices + serviceTableLines * index
        if (index !== 0) {
          totalLinesBeforeEachService = totalLinesBeforeServices + (serviceTableLines + spaceAfterEachService) * index
        }
        headers[`serviceHeader1-${index}`] = `A${totalLinesBeforeEachService + 1}:D${totalLinesBeforeEachService + 1}`
        headers[`serviceHeader2-${index}`] = `A${totalLinesBeforeEachService + 3}:D${totalLinesBeforeEachService + 3}`
        headers[`serviceRange${index}`] = `A${totalLinesBeforeEachService + 1}:D${totalLinesBeforeEachService + 4}`
        headers[`serviceDetailRow${index}`] = totalLinesBeforeEachService + 2
      })
      return headers
    }

    const totalLinesBeforeSummary = totalLinesBeforeServices + servicesDetail.length + spaceAfterServicesDetail
    const totalLinesBeforePaymentTerm = totalLinesBeforeSummary + summary.length + spaceAfterSummary
    const totalLinesBeforeTerm = totalLinesBeforePaymentTerm + paymentTerm.length + spaceAfterPaymentTerm

    const dataInfo = {
      globalStyle: 'A1:Z999',
      totalLinesBeforeCompanyInfor,
      companyInforName: `A${totalLinesBeforeCompanyInfor + 1}:A${totalLinesBeforeCompanyInfor + 1}`,
      generalInforHeader: `A${totalLinesBeforeGeneral + 1}:D${totalLinesBeforeGeneral + 1}`,
      ...getServiceHeaders(services),
      summaryHeader: `C${totalLinesBeforeSummary + 1}:C${totalLinesBeforeSummary + summary.length}`,
      summaryLastLine: `C${totalLinesBeforeSummary + summary.length}:D${totalLinesBeforeSummary + summary.length}`,
      paymentTermHeader: `A${totalLinesBeforePaymentTerm + 1}:B${totalLinesBeforePaymentTerm + 1}`,
      termHeader: `A${totalLinesBeforeTerm + 1}:D${totalLinesBeforeTerm + 1}`,
      termBody: `A${totalLinesBeforeTerm + 2}:D${totalLinesBeforeTerm + 2 + estimateLines(quoteTerm, 60)}`
    }

    return addStyles(workbookBlob, dataInfo, services)
  }

  const addStyles = (workbookBlob: any, dataInfo: any, services: any) => {
    return XlsxPopulate.fromDataAsync(workbookBlob).then((workbook: any) => {
      workbook.sheets().forEach((sheet: any) => {
        sheet.column('A').width(45)
        sheet.column('B').width(35)
        sheet.column('C').width(20)
        sheet.column('D').width(20)
        sheet.column('E').width(20)
        sheet.column('F').width(20)
        sheet.column('G').width(20)
        sheet.column('H').width(20)

        sheet.range(dataInfo.globalStyle).style({
          horizontalAlignment: 'left',
          verticalAlignment: 'top'
        })

        sheet.range(dataInfo.companyInforName).style({
          bold: true
        })

        sheet.range(dataInfo.generalInforHeader).style({
          bold: true
        })

        services.forEach((_: any, index: number) => {
          sheet.range(dataInfo[`serviceHeader1-${index}`]).style({
            bold: true,
            fontSize: 12
          })
          sheet.range(dataInfo[`serviceHeader2-${index}`]).style({
            bold: true,
            fontSize: 12
          })
          sheet.range(dataInfo[`serviceRange${index}`]).style({
            border: true,
            borderColor: 'black',
            verticalAlignment: 'top',
            wrapText: true
          })
          sheet.row(dataInfo[`serviceDetailRow${index}`]).height(70)
        })

        sheet.range(dataInfo.summaryHeader).style({
          bold: true
        })

        sheet.range(dataInfo.summaryLastLine).style({
          bold: true,
          fontColor: 'ff0000',
          topBorder: true,
          topBorderStyle: 'thin',
          bottomBorder: true,
          bottomBorderStyle: 'double',
          borderColor: 'black'
        })

        sheet.range(dataInfo.paymentTermHeader).style({
          bold: true
        })

        sheet.range(dataInfo.termHeader).merged(true).style({
          bold: true
        })

        sheet.range(dataInfo.termBody).merged(true).style({
          verticalAlignment: 'top',
          wrapText: true,
          justifyLastLine: true
        })
      })
      return workbook.outputAsync().then((workbookBlob: any) => URL.createObjectURL(workbookBlob))
    })
  }

  return (
    <div>
      <button onClick={() => createDownloadData()}>Download CSV</button>
    </div>
  )
}
